import { useEffect, useState } from "react";
import {
  listSources, addSource, rederiveSource, reingestSource, armWebhook,
  type SourcesResponse,
} from "../api";
import { GridLoader, Pill } from "./ai/Primitives";

/**
 * Sources panel — manage an instance's topic-watched code repos from the UI.
 * Lists watched repos (watch + page counts, live webhook status), adds a repo
 * (LLM-derives its watch in CI → review PR), re-derives, and arms the push
 * webhook. The config lives in code_sources.json; this is its full surface.
 */
export function SourcesPanel({
  open, instance, onClose,
}: {
  open: boolean;
  instance: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<SourcesResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [repo, setRepo] = useState("");
  const [subtree, setSubtree] = useState("src");
  const [profile, setProfile] = useState("typescript");
  const [topicsInput, setTopicsInput] = useState("");

  const refresh = () => {
    setLoading(true); setErr(null);
    listSources(instance, true).then(d => {
      setData(d);
      if (!topicsInput && d.topics.length) setTopicsInput(d.topics.join(", "));
    }).catch(e => setErr((e as Error).message)).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) refresh(); }, [open, instance]); // eslint-disable-line

  if (!open) return null;

  const onAdd = async () => {
    const topics = topicsInput.split(",").map(t => t.trim()).filter(Boolean);
    if (!repo.trim()) { setErr("Enter a repo (owner/name)."); return; }
    setBusy("add"); setErr(null); setMsg(null);
    try {
      const r = await addSource(instance, repo.trim(), subtree.trim() || "src", profile, topics);
      setMsg(`Derivation dispatched — review PR #${r.pr_number} in Changes once CI finishes.`);
      setRepo(""); setAdding(false);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  };

  const onRederive = async (r: string) => {
    setBusy("rederive:" + r); setErr(null); setMsg(null);
    try {
      const res = await rederiveSource(instance, r);
      setMsg(`Re-derivation of ${r} dispatched — PR #${res.pr_number} in Changes.`);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  };

  const onReingest = async (r: string) => {
    setBusy("reingest:" + r); setErr(null); setMsg(null);
    try {
      const res = await reingestSource(instance, r);
      if (res.pr_number) setMsg(`Re-ingest of ${r} opened PR #${res.pr_number} in Changes.`);
      else if (!res.files) setMsg(`${r} is up to date at ${(res.head || "").slice(0, 7)}.`);
      else setMsg(`Re-ingest of ${r} dispatched — ${res.files} file(s) since ${(res.base || "").slice(0, 7) || "the start"}.`);
      refresh();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  };

  const onArm = async (r: string) => {
    setBusy("arm:" + r); setErr(null); setMsg(null);
    try {
      const res = await armWebhook(instance, r);
      setMsg(res.already_armed ? `${r} webhook already armed.` : `${r} webhook armed.`);
      refresh();
    } catch (e) { setErr((e as Error).message + " — use the configure-webhook link instead."); }
    finally { setBusy(null); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">Sources — <code>{instance}</code></h2>
        <p className="modal-hint">
          Code repos watched by topic. Adding or re-deriving a repo runs the
          watch derivation in CI and opens a <strong>Changes</strong> PR.
        </p>

        {err && <div className="error-banner">{err}</div>}
        {msg && (
          <div className="provision-state">
            <Pill tone="done" dot>Done</Pill>
            <span>{msg}</span>
          </div>
        )}
        {loading && <GridLoader label="Loading sources" />}

        {data && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "0 0 14px" }}>
              <span style={{ fontSize: 13, color: "var(--ink-2)" }}>Topics</span>
              {data.topics.map(t => <span key={t} className="source-topic-chip">{t}</span>)}
              {data.topics.length === 0 && <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>None yet</span>}
            </div>

            {data.sources.length === 0 && (
              <div style={{ color: "var(--ink-3)", fontSize: 13, padding: "18px 4px" }}>No code sources yet. Add one below.</div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {data.sources.map(s => (
                <div key={s.repo} className="source-card">
                  <div className="source-card-head">
                    <span className="source-repo">{s.repo}</span>
                    <Pill tone={s.webhook === "armed" ? "done" : s.webhook === "off" ? "running" : "neutral"}
                          dot={s.webhook === "armed"}>
                      {s.webhook === "armed" ? "Webhook armed"
                        : s.webhook === "off" ? "Webhook off" : "Webhook unknown"}
                    </Pill>
                  </div>
                  <div className="source-meta">
                    <span>⎇ {s.branch}</span>
                    <span>{s.subtree}</span>
                    <span>{s.profile}</span>
                    <span>{s.watch_count} watched globs</span>
                    <span style={{ color: "var(--ink-3)" }}>{s.page_count} pages</span>
                  </div>
                  <div className="source-card-actions">
                    <button disabled={!!busy} onClick={() => onReingest(s.repo)}>
                      {busy === "reingest:" + s.repo ? "Re-ingesting…" : "Re-ingest"}
                    </button>
                    <button disabled={!!busy} onClick={() => onRederive(s.repo)}>
                      {busy === "rederive:" + s.repo ? "Re-deriving…" : "Re-derive watch"}
                    </button>
                    {s.webhook !== "armed" && (
                      <button disabled={!!busy} onClick={() => onArm(s.repo)}>
                        {busy === "arm:" + s.repo ? "Arming…" : "Arm webhook"}
                      </button>
                    )}
                    <a href={`https://github.com/${s.repo}/settings/hooks`} target="_blank" rel="noreferrer"
                       className="source-webhook-link">Configure</a>
                  </div>
                </div>
              ))}
            </div>

            {adding ? (
              <div className="source-add-form">
                <div className="source-add-row">
                  <input placeholder="owner/name" value={repo} onChange={e => setRepo(e.target.value)} style={{ flex: 2 }} />
                  <input placeholder="subtree (src)" value={subtree} onChange={e => setSubtree(e.target.value)} style={{ flex: 1 }} />
                  <select value={profile} onChange={e => setProfile(e.target.value)}>
                    <option value="typescript">typescript</option>
                    <option value="javascript">javascript</option>
                  </select>
                </div>
                <input placeholder="topics (comma-separated)" value={topicsInput}
                       onChange={e => setTopicsInput(e.target.value)} style={{ width: "100%", marginTop: 8 }} />
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button className="primary" disabled={busy === "add"} onClick={onAdd}>
                    {busy === "add" ? "Dispatching…" : "Derive watch → PR"}
                  </button>
                  <button disabled={busy === "add"} onClick={() => setAdding(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button className="source-add-btn" onClick={() => setAdding(true)}>+ Add source</button>
            )}

          </>
        )}

        <div className="modal-actions">
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
